import { DndContext, DragOverlay, MouseSensor, TouchSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownDivider, DropdownHeader, DropdownItem, DropdownMenu, Icon, Label } from "semantic-ui-react";

import { Dropdown, NavBarSearch, NavBarSearchAppointments, NavPortal, useCan } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useSearchTerm } from "components/NavBarSearch/hooks";
import { usePermanentRealTimeUser, useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { AppointmentBrandsFilter, AppointmentCard, MechanicBox, StatusFilters, UnassignedAppointmentCard, UnassignedColumn } from "modules/Dayplanner/components";
import "modules/Dayplanner/Dayplanner.scss";
import { useAssignMutation, useDayplanner, useReassignMutation, useUnassignMutation } from "modules/Dayplanner/hooks";
import { DAYPLANNER_STORAGE_KEYS } from "modules/Dayplanner/storageKeys";
import { applyFilters, ascendingSort, descendingSort, prepareDayplannerData } from "modules/Dayplanner/utils";
import { getPreference, setPreference } from "utils/common";
import { AscImg, DescImg } from "utils/staticAssets";

import type { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core";
import type { Appointment, CAR_TYPE, DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS, User } from "models";

export type DayplannerColumns = {
  appointmentsColumn: DayplannerAppointment[];
  unassignedColumn: DayplannerAppointment[];
  mechanicsColumn: MechanicWithAppointments[];
  carReadyColumn: DayplannerAppointment[];
  qualityControlColumn: DayplannerAppointment[];
};

export interface DayplannerAppointment extends Appointment {
  inactive?: boolean;
  workingOn?: User[];
}

export interface MechanicWithAppointments extends User {
  appointments: DayplannerAppointment[];
  expanded?: boolean;
  auto_expand?: boolean;
}

export enum MECHANIC_SORTING {
  Ascending = "ascending",
  Descending = "descending"
}

export enum WO_INTERNAL_FILTER {
  All = 1,
  WO,
  Internal
}

export enum DAYPLANNER_COLUMNS {
  Appointments = 1,
  Unassigned,
  Mechanics,
  CarReady,
  QualityCheck
}

enum MECHANIC_FILTERS {
  All = 1,
  HideOccupiedMechanics,
  HideUnoccupiedMechanics
}

const initialState: DayplannerColumns = {
  appointmentsColumn: [],
  unassignedColumn: [],
  mechanicsColumn: [],
  carReadyColumn: [],
  qualityControlColumn: []
};

export type BrandOption = {
  key: string;
  text: string;
  value: string;
};

const modifiers = [restrictToWindowEdges, snapCenterToCursor];

const getCollapsedColumns = (user?: User) => {
  const columns = [];
  if (user?.day_planner_appointments_collapsed_by_default) columns.push(DAYPLANNER_COLUMNS.Appointments);
  if (user?.day_planner_cars_ready_collapsed_by_default) columns.push(DAYPLANNER_COLUMNS.CarReady);
  if (user?.day_planner_quality_control_collapsed_by_default) columns.push(DAYPLANNER_COLUMNS.QualityCheck);

  return columns;
};

export const Dayplanner = () => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const vehicleTypeFilter: CAR_TYPE | null = user?.car_type_filter ?? null;

  const canUpdateAppointment = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DayplannerAppointmentAssign);
  const canListMechanics = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.DayplannerMechanics);

  const { data: brands } = useCarBrandsQuery();
  const [columns, setColumns] = useState(initialState);
  const [defaultColumns, setDefaultColumns] = useState(initialState);
  const [preparedAppointments, setPreparedAppointments] = useState<DayplannerAppointment[]>([]);
  const [collapsedColumns, setCollapsedColumns] = useState<DAYPLANNER_COLUMNS[]>(
    getPreference(DAYPLANNER_STORAGE_KEYS.CollapsedColumnsPreference, getCollapsedColumns(user))
  );
  const [numberOfCardShownInMechanicColumn, setNumberOfCardShownInMechanicColumn] = useState(3);
  const [mechanicsFilter, setMechanicsFilter] = useState<MECHANIC_FILTERS>(getPreference(DAYPLANNER_STORAGE_KEYS.SelectedMechanicPreference, MECHANIC_FILTERS.All));
  const [selectedWoInternalFilter, setSelectedWoInternalFilter] = useState<WO_INTERNAL_FILTER>(
    getPreference(DAYPLANNER_STORAGE_KEYS.SelectedWoInternalPreference, WO_INTERNAL_FILTER.All)
  );

  const [draggedAppointment, setDraggedAppointment] = useState<DayplannerAppointment | null>(null);
  const [draggedOverMechanicId, setDraggedOverMechanicId] = useState<number | null>(null);
  const [activeFilters, setActiveFilters] = useState<DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS[]>(
    getPreference(DAYPLANNER_STORAGE_KEYS.SelectedActiveStatusFiltersPreference, user?.dayplanner_appointment_status_identifiers ?? [])
  );
  const [autoExpandedMechanics, setAutoExpandedMechanics] = useState<number[]>([]);
  const [mechanicsPinned, setMechanicsPinned] = useState<number[]>(getPreference(DAYPLANNER_STORAGE_KEYS.PinnedMechanicsPreference, []));
  const [mechanicsSorting, setMechanicsSorting] = useState<MECHANIC_SORTING>(
    getPreference(DAYPLANNER_STORAGE_KEYS.MechanicSortingPreference, MECHANIC_SORTING.Ascending)
  );
  const [brandsFilter, setBrandsFilter] = useState<string[]>(getPreference(DAYPLANNER_STORAGE_KEYS.AppointmentBrandsPreference, []));
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([]);

  const appointmentsColumnRef = useRef<HTMLDivElement>(null);
  const unassignedColumnRef = useRef<HTMLDivElement>(null);
  const mechanicsColumnRef = useRef<HTMLDivElement>(null);
  const carReadyColumnRef = useRef<HTMLDivElement>(null);
  const qualityControlColumnRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } })
  );

  const { mechanics, appointments, loading, error, refetch: refetchAppointments, refetchMechanics } = useDayplanner();
  const assignMutation = useAssignMutation();
  const unassignMutation = useUnassignMutation();
  const reassignMutation = useReassignMutation();

  const { selectedLocation } = useSelectedLocation();
  const searchTerm = useSearchTerm();

  useRegisterNavAction(refetchAppointments);

  const mechanicOptions = [
    { key: MECHANIC_FILTERS.All, value: MECHANIC_FILTERS.All, text: t("v8_all", "All") },
    {
      key: MECHANIC_FILTERS.HideOccupiedMechanics,
      value: MECHANIC_FILTERS.HideOccupiedMechanics,
      text: t("v8_hide_occupied_mechanics", "Hide occupied mechanics")
    },
    {
      key: MECHANIC_FILTERS.HideUnoccupiedMechanics,
      value: MECHANIC_FILTERS.HideUnoccupiedMechanics,
      text: t("v8_hide_unoccupied_mechanics", "Hide unoccupied mechanics")
    }
  ];

  const appointmentOptions = [
    { key: WO_INTERNAL_FILTER.All, value: WO_INTERNAL_FILTER.All, text: t("v8_all", "All") },
    { key: WO_INTERNAL_FILTER.WO, value: WO_INTERNAL_FILTER.WO, text: t("v8_wo", "WO") },
    { key: WO_INTERNAL_FILTER.Internal, value: WO_INTERNAL_FILTER.Internal, text: t("v8_internal", "Internal") }
  ];

  useEffect(() => {
    document.getElementById("containerId")?.setAttribute("style", "overflow-y: hidden");

    return () => {
      document.getElementById("containerId")?.removeAttribute("style");
    };
  }, []);

  useEffect(() => {
    if (!appointments?.length) return;
    const brandDataOptions: BrandOption[] = [{ key: "all", value: "all", text: t("v8_all", "All") }];

    const dayplannerBrandGroupIds = user?.dayplanner_brand_group_ids;

    if (dayplannerBrandGroupIds?.length) {
      brands?.forEach(brand => {
        const text = `${brand.brand_group_name} - ${brand.name}`;
        if (!dayplannerBrandGroupIds.includes(brand.brand_group_id) || brandDataOptions.some(option => option.text === text)) return;

        brandDataOptions.push({ key: brand.name, value: brand.name, text });
      });
    } else {
      const brandValues = new Set(appointments.map(({ car_make }) => car_make));
      Array.from(brandValues).forEach(brand => brandDataOptions.push({ key: brand, value: brand, text: brand }));
    }

    setBrandOptions(brandDataOptions);
  }, [appointments, brands, user?.dayplanner_brand_group_ids]);

  useEffect(() => {
    if (draggedAppointment) {
      appointmentsColumnRef.current?.classList.add("-column-not-allowed-to-drop");
      carReadyColumnRef.current?.classList.add("-column-not-allowed-to-drop");
      qualityControlColumnRef.current?.classList.add("-column-not-allowed-to-drop");
    } else {
      appointmentsColumnRef.current?.classList.remove("-column-not-allowed-to-drop");
      carReadyColumnRef.current?.classList.remove("-column-not-allowed-to-drop");
      qualityControlColumnRef.current?.classList.remove("-column-not-allowed-to-drop");
    }
  }, [draggedAppointment]);

  useEffect(() => {
    if (selectedLocation?.id) {
      if (!selectedLocation.is_dayplanner_enabled) return;
      refetchAppointments();
      if (canListMechanics) refetchMechanics();
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (!loading && mechanics && appointments) {
      const { columns, dayplannerAppointments } = prepareDayplannerData(mechanics, appointments, mechanicsSorting, mechanicsPinned);

      setColumns(columns);
      setDefaultColumns(columns);
      setPreparedAppointments(dayplannerAppointments);
    }
  }, [loading, mechanics, appointments, mechanicsSorting, mechanicsPinned, selectedLocation]);

  useEffect(() => {
    if (!mechanicsColumnRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const MARGIN_VALUE = 30;
        const CARD_WIDTH = 180;
        setNumberOfCardShownInMechanicColumn(Math.floor(width / (CARD_WIDTH + MARGIN_VALUE)));
        break;
      }
    });

    resizeObserver.observe(mechanicsColumnRef.current);

    return () => resizeObserver.disconnect();
  }, [mechanicsColumnRef.current, columns.mechanicsColumn]);

  const filteredColumns = useMemo(() => {
    if (loading) return columns;

    const result = applyFilters(columns, activeFilters, searchTerm, selectedWoInternalFilter, brandsFilter);
    if (vehicleTypeFilter === null) return result;

    return {
      ...result,
      appointmentsColumn: result.appointmentsColumn.filter(appointment => appointment.vehicle_type === vehicleTypeFilter),
      unassignedColumn: result.unassignedColumn.filter(appointment => appointment.vehicle_type === vehicleTypeFilter),
      carReadyColumn: result.carReadyColumn.filter(appointment => appointment.vehicle_type === vehicleTypeFilter),
      qualityControlColumn: result.qualityControlColumn.filter(appointment => appointment.vehicle_type === vehicleTypeFilter),
      mechanicsColumn: result.mechanicsColumn.map(mechanic => ({
        ...mechanic,
        appointments: mechanic.appointments.filter(appointment => appointment.vehicle_type === vehicleTypeFilter)
      }))
    };
  }, [columns, activeFilters, searchTerm, selectedWoInternalFilter, brandsFilter, loading, vehicleTypeFilter]);

  const scrollMechanicBoxIntoView = () => {
    const mechanicToExpand = filteredColumns.mechanicsColumn.find(mechanic => mechanic.auto_expand);

    if (mechanicToExpand) document.querySelector(`.MechanicBox-wrapper-to-scroll-to-${mechanicToExpand.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleMechanicAutoExpand = (mechanicId: number) => {
    setAutoExpandedMechanics(prev => (prev.includes(mechanicId) ? prev.filter(id => id !== mechanicId) : [...prev, mechanicId]));
  };

  useEffect(() => {
    setAutoExpandedMechanics([]);
    if (searchTerm.length >= 3) scrollMechanicBoxIntoView();
  }, [searchTerm]);

  if (!selectedLocation) return null;

  if (!selectedLocation.is_dayplanner_enabled)
    return <div className="dayplanner-disabled">{t("v8_dayplanner_not_enabled_for_selected_location", "Dayplanner is not enabled for this location.")}</div>;

  if (error) return <div>Error {error.message}</div>;

  const handleSelectMechanicSorting = (value: MECHANIC_SORTING) => {
    setColumns(prevColumns => ({
      ...prevColumns,
      mechanicsColumn: [...columns.mechanicsColumn].sort(value === MECHANIC_SORTING.Ascending ? ascendingSort : descendingSort)
    }));

    setMechanicsSorting(value);
    setPreference(DAYPLANNER_STORAGE_KEYS.MechanicSortingPreference, value);
  };

  const handleToggleMechanicPin = (mechanic_id: number) => {
    const mechanicPinnedCopy = [...mechanicsPinned];
    const mechanicIndex = mechanicPinnedCopy.indexOf(mechanic_id);
    if (mechanicIndex === -1) {
      mechanicPinnedCopy.push(mechanic_id);
    } else {
      mechanicPinnedCopy.splice(mechanicIndex, 1);
    }
    setMechanicsPinned(mechanicPinnedCopy);
    setPreference(DAYPLANNER_STORAGE_KEYS.PinnedMechanicsPreference, mechanicPinnedCopy);
  };

  const handleToggleCollapseColumn = (column: DAYPLANNER_COLUMNS) => {
    const newCollapsedColumns: DAYPLANNER_COLUMNS[] = collapsedColumns.includes(column) ? collapsedColumns.filter(col => col !== column) : [...collapsedColumns, column];

    setCollapsedColumns(newCollapsedColumns);
    setPreference(DAYPLANNER_STORAGE_KEYS.CollapsedColumnsPreference, newCollapsedColumns);
  };

  const handleOverMechanic = (mechanicId: number) => {
    const mechanic = filteredColumns.mechanicsColumn.find(m => m.id === mechanicId);
    if (autoExpandedMechanics.includes(mechanicId) || mechanic?.auto_expand) return;

    setAutoExpandedMechanics(prev => [...prev, mechanicId]);
  };

  const handleWoInternalFilter = (value: WO_INTERNAL_FILTER) => {
    setSelectedWoInternalFilter(value);
    setPreference(DAYPLANNER_STORAGE_KEYS.SelectedWoInternalPreference, value);
  };

  const handleSelectMechanicFilter = (value: MECHANIC_FILTERS) => {
    setMechanicsFilter(value);
    setPreference(DAYPLANNER_STORAGE_KEYS.SelectedMechanicPreference, value);
  };

  const handleSelectStatusFilter = (selectedFilter: DAY_PLANNER_APPOINTMENT_STATUSES_FILTERS) => {
    const selectedFilters = activeFilters.includes(selectedFilter) ? activeFilters.filter(filter => filter !== selectedFilter) : [...activeFilters, selectedFilter];

    setActiveFilters(selectedFilters);
    setPreference(DAYPLANNER_STORAGE_KEYS.SelectedActiveStatusFiltersPreference, selectedFilters);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    let app: DayplannerAppointment | null = null;

    if (active.data.current?.mechanicId) {
      app = columns.mechanicsColumn.find(m => m.id === active.data.current?.mechanicId)?.appointments.find(a => a.id === active.data.current?.appointment?.id) ?? null;
    } else app = columns.unassignedColumn.find(a => a.id === active.data.current?.appointment?.id) ?? null;

    setDraggedAppointment(app ? { ...app } : null);
  };

  const resetDrag = () => {
    setDraggedAppointment(null);
    setDraggedOverMechanicId(null);
    setAutoExpandedMechanics([]);
  };

  const handleDragCancel = () => {
    setColumns(defaultColumns);
    resetDrag();
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    setDraggedOverMechanicId(over?.data.current?.mechanicId ?? null);

    // this method handle only moving an appointment from one container to another (from one mechanic to another, or unassigned to assigned, or vice versa)
    // the sorting within a container is done by dnd-kit in css and does not actually change the appointment order in our state
    // applying the sorting whithin a container is done in handleDragOver if needed
    // here we return early if we are not dragging over a different container
    if (!draggedAppointment || !over?.id || over.id === active.id || over.data.current?.mechanicId === active.data.current?.mechanicId) return;

    if (over.data.current?.unassigned) {
      if (columns.unassignedColumn.length === 0) {
        setColumns(columns => ({
          ...columns,
          unassignedColumn: columns.unassignedColumn.concat({
            ...draggedAppointment,
            assigned_mechanic: null,
            assigned_mechanic_order: null
          }),
          mechanicsColumn: columns.mechanicsColumn.map(m => ({ ...m, appointments: m.appointments.filter(a => active.id !== a.id) }))
        }));
      } else if (over.data.current.appointment && !columns.unassignedColumn.some(a => a.id === active.id)) {
        setColumns(columns => ({
          ...columns,
          unassignedColumn: columns.unassignedColumn.toSpliced(columns.unassignedColumn.findIndex(a => a.id === over.id) + 1, 0, {
            ...draggedAppointment,
            assigned_mechanic: null,
            assigned_mechanic_order: null
          }),
          mechanicsColumn: columns.mechanicsColumn.map(m => ({ ...m, appointments: m.appointments.filter(a => active.id !== a.id) }))
        }));
      }

      return;
    }

    if (over.data.current?.mechanicId && over.data.current.order) {
      if (active.data.current?.unassigned && !columns.mechanicsColumn.some(m => m.appointments.some(a => a.id === active.id))) {
        setColumns(columns => ({
          ...columns,
          unassignedColumn: columns.unassignedColumn.filter(a => active.id !== a.id),
          mechanicsColumn: columns.mechanicsColumn.map(m => {
            if (m.id === over.data.current?.mechanicId) {
              return {
                ...m,
                appointments: m.appointments
                  .map(a =>
                    Number(a.assigned_mechanic_order) >= over.data.current?.order ? { ...a, assigned_mechanic_order: Number(a.assigned_mechanic_order) + 1 } : a
                  )
                  .concat({ ...draggedAppointment, assigned_mechanic: m.id, assigned_mechanic_order: over.data.current.order })
              };
            }

            return m;
          })
        }));
      } else if (!columns.mechanicsColumn.some(m => m.id === over.data.current?.mechanicId && m.appointments.some(a => a.id === active.id))) {
        setColumns(columns => ({
          ...columns,
          mechanicsColumn: columns.mechanicsColumn.map(m => {
            if (m.id === active.data.current?.mechanicId)
              return {
                ...m,
                appointments: m.appointments
                  .filter(a => a.id !== active.id)
                  .map(a =>
                    Number(a.assigned_mechanic_order) > active.data.current?.order ? { ...a, assigned_mechanic_order: Number(a.assigned_mechanic_order) - 1 } : a
                  )
              };

            if (m.id === over.data.current?.mechanicId) {
              return {
                ...m,
                appointments: m.appointments
                  .map(a =>
                    Number(a.assigned_mechanic_order) >= over.data.current?.order ? { ...a, assigned_mechanic_order: Number(a.assigned_mechanic_order) + 1 } : a
                  )
                  .concat({ ...draggedAppointment, assigned_mechanic: m.id, assigned_mechanic_order: over.data.current.order })
              };
            }

            return m;
          })
        }));
      }
    }
  };

  const handleDragEnd = ({ over }: DragEndEvent) => {
    if (!over?.id || !draggedAppointment || (over.data.current?.unassigned && !draggedAppointment.assigned_mechanic)) {
      handleDragCancel();
      return;
    }

    if (over.data.current?.mechanicId) {
      const app = columns.mechanicsColumn.find(m => m.id == over.data.current?.mechanicId)?.appointments.find(a => a.id === draggedAppointment.id);
      if (!app?.assigned_mechanic_order) {
        handleDragCancel();
        return;
      }

      let newOrder = app.assigned_mechanic_order;
      if (!over.data.current.placeholder && app.assigned_mechanic_order !== over.data.current.order) {
        setColumns(columns => ({
          ...columns,
          mechanicsColumn: columns.mechanicsColumn.map(m => {
            if (m.id === over.data.current?.mechanicId) {
              if (Number(app.assigned_mechanic_order) < over.data.current.order) {
                return {
                  ...m,
                  appointments: m.appointments.map(a => {
                    if (a.id === app.id) return { ...a, assigned_mechanic_order: over.data.current?.order };
                    if (Number(a.assigned_mechanic_order) > Number(app.assigned_mechanic_order) && Number(a.assigned_mechanic_order) <= over.data.current?.order)
                      return { ...a, assigned_mechanic_order: Number(a.assigned_mechanic_order) - 1 };

                    return a;
                  })
                };
              } else {
                return {
                  ...m,
                  appointments: m.appointments.map(a => {
                    if (a.id === app.id) return { ...a, assigned_mechanic_order: over.data.current?.order };
                    if (Number(a.assigned_mechanic_order) >= Number(app.assigned_mechanic_order) && Number(a.assigned_mechanic_order) < over.data.current?.order)
                      return { ...a, assigned_mechanic_order: Number(a.assigned_mechanic_order) + 1 };

                    return a;
                  })
                };
              }
            }

            return m;
          })
        }));

        newOrder = over.data.current.order;
      }

      const data = { appointment_id: draggedAppointment.id, user_id: over.data.current.mechanicId, order: newOrder };
      if (canUpdateAppointment) {
        if (!draggedAppointment.assigned_mechanic_order) assignMutation(data);
        else reassignMutation(data);
      }
    } else if (over.data.current?.unassigned) {
      setDefaultColumns(columns);
      if (canUpdateAppointment) unassignMutation({ appointment_id: draggedAppointment.id });
    }

    resetDrag();
  };

  const handleBrandFilter = (value: string) => {
    if (!value || value === "all") {
      setPreference(DAYPLANNER_STORAGE_KEYS.AppointmentBrandsPreference, []);
      return setBrandsFilter([]);
    }
    setBrandsFilter(prev => {
      const newValue = prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value];
      setPreference(DAYPLANNER_STORAGE_KEYS.AppointmentBrandsPreference, newValue);

      return newValue;
    });
  };

  const isAppointmentsColumnCollapsed = collapsedColumns.includes(DAYPLANNER_COLUMNS.Appointments);
  const isCarReadyColumnCollapsed = collapsedColumns.includes(DAYPLANNER_COLUMNS.CarReady);
  const isQualityCheckColumnCollapsed = collapsedColumns.includes(DAYPLANNER_COLUMNS.QualityCheck);
  const isMechanicsWoFilterActive = selectedWoInternalFilter !== WO_INTERNAL_FILTER.All || mechanicsFilter !== MECHANIC_FILTERS.All;

  return (
    <DndContext
      sensors={sensors}
      modifiers={modifiers}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <NavPortal>
        <StatusFilters appointments={preparedAppointments} activeFilters={activeFilters} onSelect={handleSelectStatusFilter} />
        <AppointmentBrandsFilter brandsFilter={brandsFilter} brandOptions={brandOptions} setSelectedBrand={handleBrandFilter} />
        <NavBarSearch>
          <NavBarSearchAppointments />
        </NavBarSearch>
      </NavPortal>

      <div className="Dayplanner">
        <div className={`Dayplanner-columnContainer ${isAppointmentsColumnCollapsed ? "-collapsed" : ""}`}>
          <div className="Dayplanner-columnHeader">
            <h4>{isAppointmentsColumnCollapsed ? t("v8_appointments_short", "AP") : t("v8_appointments", "Appointments")}</h4>

            <Label className="collapse-column-label" onClick={() => handleToggleCollapseColumn(DAYPLANNER_COLUMNS.Appointments)}>
              <Icon className={isAppointmentsColumnCollapsed ? "arrows maximize" : "arrows minimize"} color="green" />
            </Label>
          </div>

          <div ref={appointmentsColumnRef} className="Dayplanner-column">
            {!loading &&
              filteredColumns.appointmentsColumn.map(app => (
                <AppointmentCard key={app.id} appointment={app} column={DAYPLANNER_COLUMNS.Appointments} isColumnCollapsed={isAppointmentsColumnCollapsed} />
              ))}
          </div>
        </div>

        <UnassignedColumn>
          <div className="Dayplanner-columnHeader">
            <h4>{t("v8_unassigned", "Unassigned")}</h4>
          </div>

          <div ref={unassignedColumnRef} className="Dayplanner-column">
            <SortableContext items={columns.unassignedColumn.map(a => a.id)} strategy={verticalListSortingStrategy}>
              {!loading &&
                filteredColumns.unassignedColumn.map(app => <UnassignedAppointmentCard key={app.id} appointment={app} column={DAYPLANNER_COLUMNS.Unassigned} />)}
            </SortableContext>
          </div>
        </UnassignedColumn>

        <div className="Dayplanner-columnContainer -mechanic-tasks -flex-grow-3" ref={mechanicsColumnRef}>
          <div className="Dayplanner-columnHeader">
            <h4>{t("v8_mechanics_tasks", "Mechanic's Tasks")}</h4>
            <div className="Dayplanner-columnHeader-section">
              <Dropdown button className="icon wo-mechanic-btn " floating icon={isMechanicsWoFilterActive ? "filters" : "filter"} selectOnBlur={false}>
                <DropdownMenu>
                  <DropdownHeader content={<span className="filter-menu-header-text">{t("v8_appointments", "Appointments")}</span>} />
                  {appointmentOptions.map(el => (
                    <DropdownItem key={el.key} onClick={() => handleWoInternalFilter(el.value)} selected={selectedWoInternalFilter === el.value}>
                      <span className="filter-menu-option-text"> {el.text} </span>
                    </DropdownItem>
                  ))}
                  <DropdownDivider />
                  <DropdownHeader content={<span className="filter-menu-header-text">{t("v8_mechanics", "Mechanics")}</span>} />
                  {mechanicOptions.map(el => (
                    <DropdownItem key={el.key} onClick={() => handleSelectMechanicFilter(el.value)} selected={mechanicsFilter === el.value}>
                      <span className="filter-menu-option-text"> {el.text} </span>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>

              <div>
                <button
                  className="sort-btn"
                  onClick={() => handleSelectMechanicSorting(mechanicsSorting === MECHANIC_SORTING.Ascending ? MECHANIC_SORTING.Descending : MECHANIC_SORTING.Ascending)}
                >
                  <div className="sort-btn-icon-container">
                    <img src={mechanicsSorting === MECHANIC_SORTING.Ascending ? AscImg : DescImg} className="sort-btn-icon" />
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div className="Dayplanner-column -mechanic-tasks">
            {!loading &&
              filteredColumns.mechanicsColumn.map((mechanic: MechanicWithAppointments) => {
                if (
                  (mechanicsFilter === MECHANIC_FILTERS.HideOccupiedMechanics && mechanic.appointments.length > 0) ||
                  (mechanicsFilter === MECHANIC_FILTERS.HideUnoccupiedMechanics && mechanic.appointments.length === 0)
                )
                  return null;

                return (
                  <div key={mechanic.id} className={`MechanicBox-wrapper MechanicBox-wrapper-to-scroll-to-${mechanic.id}`}>
                    <MechanicBox
                      activeFilters={activeFilters}
                      searchTerm={searchTerm}
                      mechanic={mechanic}
                      key={mechanic.id}
                      mechanicsPinned={mechanicsPinned}
                      onToggleMechanicPin={handleToggleMechanicPin}
                      numberOfCardsPerRow={numberOfCardShownInMechanicColumn}
                      onOver={handleOverMechanic}
                      onToggleMechanicAutoExpand={toggleMechanicAutoExpand}
                      autoExpand={autoExpandedMechanics.includes(mechanic.id) !== Boolean(mechanic.auto_expand)}
                      draggedOverMechanicId={draggedOverMechanicId}
                    />
                  </div>
                );
              })}
          </div>
        </div>

        <div className={`Dayplanner-columnContainer ${isCarReadyColumnCollapsed ? "-collapsed" : ""}`}>
          <div className="Dayplanner-columnHeader">
            <h4>{isCarReadyColumnCollapsed ? t("v8_cars_ready_short", "CR") : t("v8_cars_ready", "Cars Ready")}</h4>

            <Label className="collapse-column-label" onClick={() => handleToggleCollapseColumn(DAYPLANNER_COLUMNS.CarReady)}>
              <Icon className={isCarReadyColumnCollapsed ? "arrows maximize" : "arrows minimize"} color="green" />
            </Label>
          </div>

          <div ref={carReadyColumnRef} className="Dayplanner-column">
            {!loading &&
              filteredColumns.carReadyColumn.map(app => (
                <AppointmentCard
                  key={app.id}
                  appointment={app}
                  column={DAYPLANNER_COLUMNS.CarReady}
                  isColumnCollapsed={isCarReadyColumnCollapsed}
                  mechanic={columns.mechanicsColumn.find(m => m.id === app.last_assigned_mechanic)}
                />
              ))}
          </div>
        </div>

        <div className={`Dayplanner-columnContainer ${isQualityCheckColumnCollapsed ? "-collapsed" : ""}`}>
          <div className="Dayplanner-columnHeader">
            <h4>{isQualityCheckColumnCollapsed ? t("v8_quality_control_short", "QC") : t("v8_quality_control", "Quality Control")}</h4>

            <Label className="collapse-column-label" onClick={() => handleToggleCollapseColumn(DAYPLANNER_COLUMNS.QualityCheck)}>
              <Icon className={isQualityCheckColumnCollapsed ? "arrows maximize" : "arrows minimize"} color="green" />
            </Label>
          </div>

          <div ref={qualityControlColumnRef} className="Dayplanner-column">
            {!loading &&
              filteredColumns.qualityControlColumn.map(app => (
                <AppointmentCard
                  key={app.id}
                  appointment={app}
                  column={DAYPLANNER_COLUMNS.QualityCheck}
                  isColumnCollapsed={isQualityCheckColumnCollapsed}
                  mechanic={columns.mechanicsColumn.find(m => m.id === app.planning_mechanic?.id)}
                />
              ))}
          </div>
        </div>
      </div>
      <DragOverlay>
        {draggedAppointment ? <AppointmentCard appointment={draggedAppointment} column={DAYPLANNER_COLUMNS.Appointments} isColumnCollapsed={false} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
